import React, { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Formik } from 'formik'
import cn from 'classnames'

import { Tabs } from '@infologistics/frontend-libraries'

import { useAppDispatch, useAppSelector } from '@hooks'
import { setSignatureMarks, setNewSignature } from '@store/modules/shared'
import { resetModal } from '@store/modules/modal'
import { MarkType, Modal } from '@const/consts'
import { getIsMobile } from '@utils/utils'
import { getIsNextTask, getRecipient } from '@utils/envelope'
import SignatureModalWrapper from './components/SignatureModalWrapper'
import SignatureDraw from './components/SignatureDraw'
import SignatureUpload from './components/SignatureUpload'
import { GetSignatureTabsContent, ISignatureValue, ISignatureModalProps as IProps, ISignatureFormikProps } from './types'

import styles from './SignatureModal.module.scss'
import SignatureChoose from './components/SignatureChoose'
import { Nullable } from '@app/store/types'

const getDefaultModalId = (
  newSignature: Nullable<string>,
  drawSignatureTrim: Nullable<string>,
  signatureImg: Nullable<string> | undefined,
  isMobile: boolean,
) => {
  if (newSignature) {
    switch (newSignature) {
      case signatureImg:
        return Modal.SIGNATURE_IMAGE_CHOOSE;
      case drawSignatureTrim:
        return Modal.SIGNATURE_IMAGE_DRAW;
      default: {
        if (isMobile) {
          return Modal.SIGNATURE_IMAGE_DRAW;
        } else {
          return Modal.SIGNATURE_IMAGE_UPLOAD;
        }
      }
    }
  } else {
    if (signatureImg) {
      return Modal.SIGNATURE_IMAGE_CHOOSE;
    }

    return Modal.SIGNATURE_IMAGE_DRAW;
  }
};

const SignatureModal: FC<IProps> = ({ currentSignature, setFieldValue }) => {
  const { t } = useTranslation(['envelope', 'common'])
  const { signatureMarks, signatureMarksNextTask, signatures, signaturesNextTask } = useAppSelector(state => state.shared)
  const isNextTask = getIsNextTask()
  const { newSignature, drawSignatureTrim } = isNextTask ? signaturesNextTask : signatures
  const signatureImg = useAppSelector(state => state.shared.envelope.accessAuthorizedTo.user?.electronicSignatureImage);
  const [modalID, setModalID] = useState(Modal.SIGNATURE_IMAGE_DRAW);

  const isMobile = getIsMobile();

  useEffect(() => {
    setModalID(
      getDefaultModalId(
        newSignature,
        drawSignatureTrim,
        signatureImg,
        isMobile,
      ),
    );
  }, []);

  const dispatch = useAppDispatch()

  const hasSignature = getRecipient()?.prepositionedMarks.find(mark => mark.type === MarkType.SIGNATURE)
  const tabs = [
    ...(signatureImg
      ? [
          {
            name: Modal.SIGNATURE_IMAGE_CHOOSE,
            translation: t('envelope:signature.choose'),
          },
        ]
      : []),
    {
      name: Modal.SIGNATURE_IMAGE_DRAW,
      translation: t('envelope:signature.draw'),
    },
    ...(!isMobile
      ? [
          {
            name: Modal.SIGNATURE_IMAGE_UPLOAD,
            translation: t('envelope:signature.upload'),
          },
        ]
      : []),
  ];

  const getTabsContent: GetSignatureTabsContent = (
    formikProps: ISignatureFormikProps,
  ) => {
    return {
      [Modal.SIGNATURE_IMAGE_CHOOSE]: (
        <div className={"full-width"}>
          <SignatureChoose {...formikProps} />
        </div>
      ),
      [Modal.SIGNATURE_IMAGE_DRAW]: (
        <div className={"full-width"}>
          <SignatureDraw {...formikProps} />
        </div>
      ),
      [Modal.SIGNATURE_IMAGE_UPLOAD]: (
        <div className="d-flex flex-column full-width align-items-start">
          <div className={cn(styles.signature_content, "mt-4 full-width")}>
            <SignatureUpload {...formikProps} />
          </div>
        </div>
      ),
    };
  };

  const handleSubmit = (values: ISignatureValue) => {
    const { newSignature } = values

    dispatch(setNewSignature({ value: newSignature, isNextTask }))
    dispatch(resetModal())

    const signatures = isNextTask ? signatureMarksNextTask : signatureMarks
    if (currentSignature) {
      dispatch(setSignatureMarks({ signatureMarks: {...signatures, [currentSignature]: true}, isNextTask }))
      setFieldValue(`${isNextTask ? 'nextTaskMarks' : 'marks'}.${currentSignature}.value`, newSignature)
    }
  }

  return (
    <Formik
      initialValues={{
        newSignature: newSignature ?? '',
      }}
      onSubmit={handleSubmit}
    >
      {(props) => (
        <form noValidate onSubmit={props.handleSubmit}>
          <SignatureModalWrapper
            hasSignature={!!hasSignature}
            currentSignature={props.values.newSignature}
          >
            {hasSignature && (
              <Tabs
                content={getTabsContent(props)}
                tabs={tabs}
                theme='flat'
                activeTab={modalID}
                classes={styles.signature_tabs}
              />
            )}
          </SignatureModalWrapper>
        </form>
      )}
    </Formik>
  );
}

export default SignatureModal
